import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { useAppContext } from 'contexts/AppContext';
import { useDashboardsForWorkspace } from 'queries/hooks/useDashboardsForWorkspace';
import { useWorkspace } from 'queries/hooks/useWorkspace';
import { useWorkspaceVariables } from 'queries/hooks/useWorkspaceVariables';
import { dashboardsSortedByWorkspaceOrder } from 'queries/utils/dashboardSorted';
import { useEffect } from 'react';
import { useMatch } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useWorkspaceCanWrite } from 'services/AccessControlService';
import { AttachedInstruction } from './DashboardTreeHitbox';
import { GhostTreeItem, TreeItem as TreeItemComponent } from './DashboardTreeItem';
import { getAllDashboardIdsInNestedList, tree } from './DashboardTreeUtils';
import { useApplyDashboardInstruction } from './useApplyDashboardInstruction';
import { useFoldersOpen, useSetFolderOpen } from './useFoldersOpen';


export const DashboardTree: React.FC = () => {
    const { currentWorkspaceID } = useAppContext();
    const { data: workspace } = useWorkspace(currentWorkspaceID);

    const { data: dashboards = [], isFetched } = useDashboardsForWorkspace(currentWorkspaceID, {
        suspense: true,
        enabled: Boolean(workspace),
        select: dashboardsSortedByWorkspaceOrder(workspace?.data.properties?.dashboardIdOrder)
    });

    // Preload variables for all dashboards in the workspace
    useWorkspaceVariables(currentWorkspaceID, getAllDashboardIdsInNestedList(dashboards ?? []));

    const applyInstruction = useApplyDashboardInstruction();

    const { data: canWrite } = useWorkspaceCanWrite(currentWorkspaceID ?? '', { suspense: true });

    useEffect(() => {
        return monitorForElements({
            canMonitor: ({ source }) => source.data.treeId === 'navbar-dashboard-tree' && Boolean(canWrite),
            onDrop(args) {
                const { location, source } = args;
                // didn't drop on anything
                if (!location.current.dropTargets.length) {
                    return;
                }

                const itemId = source.data.id as string;

                const { id, instruction } = location.current.dropTargets[0].data as AttachedInstruction;

                if (instruction) {
                    applyInstruction({
                        instruction,
                        itemId,
                        targetId: id
                    });
                }
            }
        });
    }, [applyInstruction, canWrite]);

    const { data: openFolders = {} } = useFoldersOpen({ suspense: true });

    const dashboardPage = useMatch('/dashboard/:id');

    const [params, setParams] = useSearchParams();

    const setOpen = useSetFolderOpen();

    useEffect(() => {
        if (dashboardPage?.params?.id && isFetched) {
            setOpen(
                tree
                    .getPathToItem({
                        fullItem: true,
                        targetId: dashboardPage.params.id!,
                        current: dashboards
                    })
                    ?.map((item) => ({ itemId: item.id, open: true })) || []
            );
        }
        // We don't want to open the folders if the dashboard object updates
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboardPage, isFetched]);

    useEffect(() => {
        // If we have force_open_folder set then we will open the folders if the dashboard object updates
        // This can help avoid race conditions when installing OOB dashboards
        if (params.get('force_open_folder') === 'true' && tree.find(dashboards, dashboardPage?.params.id || '')) {
            setOpen(
                tree
                    .getPathToItem({
                        fullItem: true,
                        targetId: dashboardPage!.params.id!,
                        current: dashboards
                    })
                    ?.map((item) => ({ itemId: item.id, open: true })) || []
            );
            params.delete('force_open_folder');
            setParams(params);
        }
    }, [dashboardPage, isFetched, params, dashboards, setOpen, setParams]);

    if (dashboards.length === 0) {
        return <p className='pl-[41px]'>No dashboards</p>;
    }

    const { item: finalItem, depth: finalDepth } = tree.getLastExpandedItemWithDepth(
        openFolders[currentWorkspaceID || ''] ?? {},
        dashboards.at(-1)!
    );

    return (
        <div className='relative flex flex-col' id='dashboard-folder-tree'>
            {dashboards.map((item, index) => (
                <TreeItemComponent
                    item={item}
                    level={0}
                    isLastOfGroup={index === dashboards.length - 1}
                    key={item.id}
                    index={index}
                    treeId={'navbar-dashboard-tree'}
                />
            ))}
            <GhostTreeItem level={finalDepth} item={finalItem} treeId='navbar-dashboard-tree' />
        </div>
    );
};
